import React from "react";
import { ArrowUpRight, Sparkles } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { WHATSAPP_URL } from "@/i18n/translations";
import { trackCTA, trackWhatsApp } from "@/lib/analytics";

const HERO_BG =
  "https://images.pexels.com/photos/35024222/pexels-photo-35024222.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600";

const Hero = () => {
  const { t, isRTL } = useLang();
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative isolate overflow-hidden min-h-[88vh] sm:min-h-[92vh] flex items-center pt-24 sm:pt-32 pb-12 sm:pb-20"
    >
      {/* Background image */}
      <div
        aria-hidden
        className="absolute inset-0 bg-center bg-cover opacity-40"
        style={{ backgroundImage: `url('${HERO_BG}')` }}
      />
      <div
        aria-hidden
        className="absolute inset-0 bg-gradient-to-r from-[#0A0A0B] via-[#0A0A0B]/95 to-[#0A0A0B]/40"
      />
      <div
        aria-hidden
        className="absolute -top-40 -right-40 w-[700px] h-[700px] rounded-full"
        style={{
          background:
            "radial-gradient(closest-side, rgba(212,111,77,0.25), rgba(212,111,77,0) 70%)",
        }}
      />
      <div aria-hidden className="absolute inset-0 grain pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 md:px-10 w-full">
        <div className="max-w-3xl">
          <div
            data-testid="hero-eyebrow"
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-sand-soft bg-white/[0.02] backdrop-blur-sm max-w-full"
          >
            <Sparkles size={12} className="text-terracotta flex-shrink-0" />
            <span className="eyebrow text-[10px] sm:text-xs whitespace-normal sm:whitespace-nowrap">
              {t.hero.eyebrow}
            </span>
          </div>

          <h1
            data-testid="hero-title"
            className="mt-6 sm:mt-8 font-display font-light tracking-tighter text-maif leading-[1.02] sm:leading-[0.95] text-[44px] sm:text-7xl lg:text-[88px]"
          >
            {t.hero.title_pre}{" "}
            <span className="font-accent italic text-terracotta">{t.hero.title_accent}</span>{" "}
            {t.hero.title_post}
          </h1>

          <p
            data-testid="hero-subtitle"
            className="mt-6 sm:mt-8 max-w-xl text-base md:text-lg leading-relaxed text-maif-muted"
          >
            {t.hero.subtitle}
          </p>

          <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-4">
            <button
              onClick={() => { trackCTA("start_project", "hero"); scrollTo("lead"); }}
              data-testid="hero-cta-primary"
              className="btn-primary group justify-center"
            >
              {t.hero.primary}
              <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-secondary"
              onClick={() => trackWhatsApp("hero")}
              className="btn-secondary justify-center"
            >
              {t.hero.secondary}
            </a>
          </div>

          <p className="mt-5 sm:mt-6 text-xs text-maif-muted/80">{t.hero.micro}</p>
        </div>

        {/* Floating stat card on the side (right in LTR, left in RTL) */}
        <div
          className={`hidden lg:block absolute bottom-16 w-[320px] ${
            isRTL ? "left-10" : "right-10"
          }`}
        >
          <div className="card-surface p-6 backdrop-blur-xl bg-[#111114]/70">
            <div className="flex items-center justify-between">
              <span className="eyebrow">{t.hero.live.label}</span>
              <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
            </div>
            <div className="mt-4 font-display text-4xl text-maif tracking-tight">
              {t.hero.live.value}
            </div>
            <div className="text-sm text-maif-muted mt-1">{t.hero.live.caption}</div>
            <div className="hairline mt-5" />
            <div className="mt-4 flex items-center justify-between text-xs text-maif-muted">
              <span>{t.hero.live.responseLabel}</span>
              <span className="text-maif">{t.hero.live.responseValue}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
